import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { envConfig } from '#constants/envConfig';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';

/**
 * Brings the router's auth context in line with storage right after the app starts.
 *
 * The app hydrates from a shell prerendered at build time, and that snapshot carries
 * `isAuth: false` — nobody is signed in when a bundle is built. Reading the token before the
 * router is created (see router.tsx) fixes the route guards, but hydration still restores the
 * snapshot's context for the first render, so everything that *renders* on `isAuth` behaves as
 * if the user were a guest: the dashboard drops its chrome and the bottom navigation simply
 * isn't there until some navigation re-runs the check. That is the "where did my nav bar go
 * until I tap the logo" symptom.
 *
 * One invalidation re-runs the root beforeLoad, which in the app reads the stored JWT. It fires
 * only when the context disagrees with storage, and it can no longer bounce anyone to sign-in
 * (the guard consults storage itself now), so the correction is a single re-render rather than
 * a visible jump. Latched by a ref so a router that keeps disagreeing can't loop.
 */
export const useNativeAuthRehydrate = () => {
  const router = useRouter();
  const isAuth = useIsAuth();
  const rehydrated = useRef(false);

  useEffect(() => {
    if (!envConfig.IS_NATIVE || isAuth || rehydrated.current) {
      return;
    }

    void (async () => {
      const { getNativeToken } = await import('#features/api/utils/nativeAuth');
      if (!(await getNativeToken())) {
        // Genuinely signed out — the snapshot happens to be right.
        return;
      }
      rehydrated.current = true;
      await router.invalidate();
    })();
  }, [isAuth, router]);
};
